import React from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { useAppDispatch, useAppSelector } from '@/store';
import ClientDetection from '@/modules/casino/utils/ClientDetection';
import { connectAppNotification } from '@/modules/casino/store/actions/profile';
import ExpiringLocalStorage from '@/modules/casino/utils/ExpiringLocalStorage';

//import './index.scss';

type TemplateProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const MobileApps = (componentProps: TemplateProps) => {
  // let props = componentProps;

  const client_player_id = useAppSelector((state) => state.profile.client_player_id);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

  const [currentStep, setCurrentStep] = React.useState<string>('default'); // 'default' | 'install_app' | 'connect_app'
  const [notificationSent, setNotificationSent] = React.useState<boolean>(false);

  const MAX_NOTIFICATION_COOLDOWN_TIMER = 4 * 60 * 60; // 4 hours in seconds
  const MAX_NOTIFICATION_COOLDOWN_COUNT = 5; // 4 notifications in 4 hours
  const linkAndroid = window.config.mobileApps?.android?.url ?? '';
  const linkIOS = window.config.mobileApps?.ios?.url ?? '';
  const linkHuawei = window.config.mobileApps?.huawei?.url ?? '';

  const [cooldown, setCooldown] = React.useState<number>(0);
  const [notificationCooldown, setNotificationCooldown] = React.useState<number>(ExpiringLocalStorage.get('mobile_apps_notification_cooldown') ?? 0);

  const dispatch = useAppDispatch();

  const incrementNotificationCooldown = React.useCallback(() => {
    setNotificationCooldown((prev) => {
      const newCooldown = prev + 1;
      ExpiringLocalStorage.set('mobile_apps_notification_cooldown', newCooldown, MAX_NOTIFICATION_COOLDOWN_TIMER); // Store for 4 hours
      return newCooldown;
    });
  }, [MAX_NOTIFICATION_COOLDOWN_TIMER]);

  React.useEffect(() => {
    GTMEvent({ event: 'mobile_apps_loaded', os: os.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  },[cooldown]);

  const GTMEvent = React.useCallback((payload: { event: string; os: string }) => {
    // @ts-ignore
    if (!window.dataLayer) {
      return;
    }
    // Push data to GTM
    // @ts-ignore
    window.dataLayer.push(payload);
  }, []);

  const os = React.useMemo(() => {
    return ClientDetection.getOS();
  }, []);

  const isDisabledConnectApp = React.useMemo(() => {
    return !client_player_id || cooldown > 0 || notificationCooldown >= MAX_NOTIFICATION_COOLDOWN_COUNT;
  }, [client_player_id, cooldown, notificationCooldown]);

  const warningMessage = React.useMemo(() => {
    if (!client_player_id) {
      return 'MOBILE-APPS.NOT_LOGGED_IN';
    }
    if (notificationSent) {
      return 'MOBILE-APPS.NOTIFICATION_SENT';
    }

    return false;
  }, [client_player_id, notificationSent]);

  const nextButtonText = React.useMemo(() => {
    return 'MOBILE-APPS.STEP-' + currentStep.toUpperCase() + '.NEXT';
  }, [currentStep]);

  const infoMessage = React.useMemo(() => {
    if (cooldown > 0) {
      return 'MOBILE-APPS.{{TIMER_COOLDOWN}}';
    }
    return false;
  }, [cooldown]);

  const connectApp = React.useCallback(() => {
    if (isDisabledConnectApp) {
      return;
    }
    GTMEvent({ event: 'mobile_apps_connect_button', os: os.name });
    dispatch(connectAppNotification());
    setIsModalOpen(true);
    setCooldown(window.config.resendSMSTimer ?? 60);
    incrementNotificationCooldown();
    setNotificationSent(true);
  }, [GTMEvent, dispatch, incrementNotificationCooldown, isDisabledConnectApp, os.name]);

  const isBackButtonDisabled = React.useMemo(() => {
    return currentStep === 'default';
  }, [currentStep]);

  const backButtonHandler = React.useCallback(() => {
    if (isBackButtonDisabled) {
      return;
    }
    setCurrentStep((prev) => {
      if (prev === 'connect_app') {
        return 'install_app';
      } else if (prev === 'install_app') {
        return 'default';
      }
      return prev;
    });
  },[isBackButtonDisabled]);

  const isAndroid = React.useMemo(() => {
    return os.name === 'android';
  }, [os.name]);

  const isIOS = React.useMemo(() => {
    return os.name === 'iOS';
  }, [os.name]);

  const isHuawei = React.useMemo(() => {
    return ClientDetection.isHuawei();
  }, []);

  const isUnknown = React.useMemo(() => {
    return !isAndroid && !isIOS && !isHuawei;
  }, [isAndroid, isHuawei, isIOS]);

  const toggleModal = React.useCallback(() => {
    setIsModalOpen((prev) => !prev);
  }, []);

  const nextButtonHandler = React.useCallback(() => {
    if (currentStep === 'default') {
      if (isIOS && linkIOS) {
        // open App Store
        setCurrentStep('connect_app');
        window.open(linkIOS, '_blank');
      } else if (isHuawei && linkHuawei) {
        // open Huawei App Gallery
        setCurrentStep('connect_app');
        window.open(linkHuawei, '_blank');
      } else if (isAndroid && linkAndroid) {
        window.open(linkAndroid, '_blank');
        // open Google Play Store
        setCurrentStep('connect_app');
      } else {
        // go to the next step
        setCurrentStep('install_app');
      }
    } else if (currentStep === 'install_app') {
      // send connect app notification

      setCurrentStep('connect_app');
    } else if (currentStep === 'connect_app') {
      connectApp();
    }
  },[connectApp, currentStep, isAndroid, isHuawei, isIOS, linkAndroid, linkHuawei, linkIOS]);

  const isNextButtonDisabled = React.useMemo(() => {
    return isDisabledConnectApp && currentStep === 'connect_app';
  }, [currentStep, isDisabledConnectApp]);

  const translatePlaceholders = React.useMemo(() => ({
    TIMER_COOLDOWN: cooldown,
  }),[cooldown]);

  const contextValue = React.useMemo(() => {
    return {
      isNextButtonDisabled,
      nextButtonHandler,
      isAndroid,
      isHuawei,
      isIOS,
      isUnknown,
      translatePlaceholders,
      linkAndroid,
      linkIOS,
      linkHuawei,
      currentStep,
      warningMessage,
      infoMessage,
      isBackButtonDisabled,
      backButtonHandler,
      nextButtonText,
    };
    // }, [dataElementContext, componentProps]);
  }, [backButtonHandler, currentStep, infoMessage, isAndroid, isBackButtonDisabled, isHuawei, isIOS, isNextButtonDisabled, isUnknown, linkAndroid, linkHuawei, linkIOS, nextButtonHandler, nextButtonText, translatePlaceholders, warningMessage]);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default MobileApps;
